import { useEffect, useRef, useState } from 'react';
import {
  FileDown, ChevronDown, Users, UserCheck, BarChart2,
  FileSpreadsheet, FileText, Printer, Search, X,
  Phone, MapPin, Briefcase, Bot, TrendingUp, Instagram,
  Facebook, Globe, RefreshCw, Calendar, MessageCircle, ArrowLeft, Send
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';

const API_URL = 'https://mp3nutrition.lafzai.in/api/v1/nutrition/leads';

const platformIcon = (platform) => {
  if (!platform) return <Globe className='w-3.5 h-3.5 text-gray-400' />;
  if (platform.toLowerCase() === 'instagram') return <Instagram className='w-3.5 h-3.5 text-pink-500' />;
  if (platform.toLowerCase() === 'facebook') return <Facebook className='w-3.5 h-3.5 text-blue-600' />;
  return <Globe className='w-3.5 h-3.5 text-gray-400' />;
};

const ITEMS_PER_PAGE = 10;

const AiLeads = () => {
  const [data, setData] = useState({ service_leads: [], business_leads: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('service'); // 'service' | 'business'
  const [search, setSearch] = useState('');
  const [platformFilter, setPlatformFilter] = useState('All');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Chat history drawer
  const [chatOpen, setChatOpen] = useState(false);
  const [chatLead, setChatLead] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const chatBottomRef = useRef(null);

  const todayStr = new Date().toISOString().split('T')[0];
  const currentYearMonth = todayStr.substring(0, 7);

  // Date filter
  const [dateFilterType, setDateFilterType] = useState('all'); // 'all' | 'today' | 'month' | 'custom'
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);
  const [selectedMonth, setSelectedMonth] = useState(currentYearMonth);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error('Failed to fetch leads');
      const json = await res.json();
      setData(json);
    } catch (err) {
      toast.error('Failed to load AI leads. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatHistory = async (lead) => {
    if (!lead.platform || !lead.id) {
      toast.error('No platform / user ID available for this lead.');
      return;
    }
    setChatLead(lead);
    setChatOpen(true);
    setChatMessages([]);
    setChatError(null);
    setChatLoading(true);
    try {
      const url = `https://mp3nutrition.lafzai.in/api/v1/messages/history?platform=${lead.platform}&user_id=${lead.id}&limit=100&offset=1`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch chat history');
      const json = await res.json();
      // Sort oldest → newest for chat display
      const sorted = [...(json.messages || [])].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      setChatMessages(sorted);
    } catch (err) {
      setChatError('Could not load chat history.');
      console.error(err);
    } finally {
      setChatLoading(false);
    }
  };

  const handleContactClick = (e, lead) => {
    e.preventDefault();
    setReplyText('');
    fetchChatHistory(lead);
  };

  const sendReply = async () => {
    const msg = replyText.trim();
    if (!msg || !chatLead) return;
    if (!chatLead.platform || !chatLead.platform_user_id) {
      toast.error('Cannot send: missing platform or sender ID for this lead.');
      return;
    }
    setReplySending(true);
    try {
      const res = await fetch('https://mp3nutrition.lafzai.in/api/v1/messages/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform: chatLead.platform,
          sender_id: chatLead.platform_user_id,
          message: msg,
        }),
      });
      if (!res.ok) throw new Error('Send failed');
      setReplyText('');
      toast.success('Message sent!');
      // Optimistically append the sent message
      const optimistic = {
        id: Date.now(),
        platform: chatLead.platform,
        sender_id: chatLead.platform_user_id,
        direction: 'outbound',
        message_type: 'text',
        content: msg,
        created_at: new Date().toISOString(),
        user_display_name: 'You',
      };
      setChatMessages(prev => [...prev, optimistic]);
      setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
      console.error(err);
    } finally {
      setReplySending(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  // Reset page on tab/filter/date change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, search, platformFilter, dateFilterType, fromDate, toDate, selectedMonth]);

  const rawLeads = activeTab === 'service' ? data.service_leads : data.business_leads;

  const uniquePlatforms = ['All', ...new Set(rawLeads.map(l => l.platform).filter(Boolean))];

  const filteredLeads = rawLeads.filter(lead => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (lead.name || '').toLowerCase().includes(q) ||
      (lead.contact || '').toLowerCase().includes(q) ||
      (lead.service_type || '').toLowerCase().includes(q) ||
      (lead.location || '').toLowerCase().includes(q) ||
      (lead.current_working || '').toLowerCase().includes(q);

    const matchPlatform =
      platformFilter === 'All' || (lead.platform || '').toLowerCase() === platformFilter.toLowerCase();

    // Date filter by created_at
    let matchDate = true;
    if (lead.created_at && dateFilterType !== 'all') {
      const leadDate = lead.created_at.split('T')[0]; // 'YYYY-MM-DD'
      if (dateFilterType === 'today') matchDate = leadDate === todayStr;
      else if (dateFilterType === 'month') matchDate = leadDate.startsWith(selectedMonth);
      else if (dateFilterType === 'custom') matchDate = leadDate >= fromDate && leadDate <= toDate;
    }

    return matchSearch && matchPlatform && matchDate;
  });

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / ITEMS_PER_PAGE));
  const paginated = filteredLeads.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // ─── Export ────────────────────────────────────────────────────────────────
  const buildExportRows = (leads) => {
    if (activeTab === 'service') {
      return leads.map(l => ({
        '#': l.id,
        'Name': l.name || 'N/A',
        'Service Type': l.service_type || 'N/A',
        'Age': l.age || 'N/A',
        'Gender': l.gender || 'N/A',
        'Weight': l.weight ? `${l.weight} kg` : 'N/A',
        'Contact': l.contact || 'N/A',
        'Platform': l.platform || 'N/A',
        'Platform User ID': l.platform_user_id || 'N/A',
        'Address': l.address || 'N/A',
        'Meeting Time': l.meeting_time || 'N/A',
        'Created At': l.created_at ? new Date(l.created_at).toLocaleDateString() : 'N/A',
      }));
    } else {
      return leads.map(l => ({
        '#': l.id,
        'Name': l.name || 'N/A',
        'Age': l.age || 'N/A',
        'Location': l.location || 'N/A',
        'Current Working': l.current_working || 'N/A',
        'Contact': l.contact || 'N/A',
        'Existing Business': l.is_existing_business ? 'Yes' : 'No',
        'Business Name': l.business_name || 'N/A',
        'Established Year': l.established_year || 'N/A',
        'Platform': l.platform || 'N/A',
        'Platform User ID': l.platform_user_id || 'N/A',
        'Created At': l.created_at ? new Date(l.created_at).toLocaleDateString() : 'N/A',
      }));
    }
  };

  const handleExport = (format) => {
    const rows = buildExportRows(filteredLeads);
    const title = `${activeTab === 'service' ? 'Service' : 'Business'} Leads Report`;

    if (format === 'Excel' || format === 'CSV') {
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, title);
      const ext = format === 'Excel' ? 'xlsx' : 'csv';
      XLSX.writeFile(wb, `AI_Leads_${activeTab}_${todayStr}.${ext}`);
    } else if (format === 'PDF') {
      const doc = new jsPDF('landscape');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(title, 14, 15);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Exported: ${new Date().toLocaleString()}`, 14, 22);

      const headers = Object.keys(rows[0] || {});
      const body = rows.map(r => headers.map(h => String(r[h] ?? 'N/A')));

      autoTable(doc, {
        head: [headers],
        body,
        startY: 28,
        styles: { fontSize: 7, textColor: [40, 40, 40] },
        headStyles: { fillColor: [99, 102, 241], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 255] },
      });
      doc.save(`AI_Leads_${activeTab}_${todayStr}.pdf`);
    } else if (format === 'Print') {
      window.print();
    }

    setShowExportMenu(false);
    toast.success(`Exported as ${format}`);
  };

  // ─── Summary cards data ────────────────────────────────────────────────────
  const summaryCards = [
    {
      label: 'Total Leads',
      value: data.total_leads || 0,
      icon: <BarChart2 className='w-4 h-4' />,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
      dark: false,
    },
    {
      label: 'Service Leads',
      value: data.total_service_leads || 0,
      icon: <UserCheck className='w-4 h-4' />,
      color: 'text-green-600',
      bg: 'bg-green-50',
      dark: false,
    },
    {
      label: 'Business Leads',
      value: data.total_business_leads || 0,
      icon: <TrendingUp className='w-4 h-4' />,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      dark: false,
    },
    {
      label: 'Showing (Filtered)',
      value: filteredLeads.length,
      icon: <Users className='w-4 h-4' />,
      color: 'text-white',
      bg: 'bg-neutral-900',
      dark: true,
    },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className='max-w-full mx-auto px-4 py-5 md:px-6 md:py-6 bg-gray-50 min-h-screen'>

      {/* Header */}
      <div className='mb-4 md:mb-6 flex flex-col sm:flex-row justify-between items-start gap-3'>
        <div className='flex items-center gap-3'>
          <div className='p-2 bg-indigo-100 rounded-xl'>
            <Bot className='w-6 h-6 text-indigo-600' />
          </div>
          <div>
            <h1 className='text-[18px] md:text-2xl lg:text-3xl font-bold text-gray-900 mb-0.5'>
              AI Leads
            </h1>
            <p className='text-[11px] md:text-sm text-gray-500'>Leads generated via Lafz AI platform</p>
          </div>
        </div>

        <div className='flex flex-wrap gap-2'>
          <button
            onClick={fetchLeads}
            className='flex items-center gap-2 px-3 md:px-4 h-9 md:h-10 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all font-medium text-[11px] md:text-sm text-gray-700'
          >
            <RefreshCw className='w-4 h-4' />
            <span className='hidden md:inline'>Refresh</span>
          </button>

          <div className='relative'>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className='flex items-center gap-2 px-3 md:px-4 h-9 md:h-10 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all font-medium text-[11px] md:text-sm'
            >
              <FileDown className='w-4 h-4' />
              <span>Export</span>
              <ChevronDown className='w-4 h-4' />
            </button>
            {showExportMenu && (
              <div className='absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20'>
                {[
                  { label: 'Excel Report', fmt: 'Excel', icon: <FileSpreadsheet className='w-4 h-4 text-green-600' /> },
                  { label: 'CSV File', fmt: 'CSV', icon: <FileText className='w-4 h-4 text-blue-600' /> },
                  { label: 'PDF Document', fmt: 'PDF', icon: <FileDown className='w-4 h-4 text-red-600' /> },
                  { label: 'Print', fmt: 'Print', icon: <Printer className='w-4 h-4 text-gray-600' /> },
                ].map(({ label, fmt, icon }) => (
                  <button
                    key={fmt}
                    onClick={() => handleExport(fmt)}
                    className='w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm flex items-center gap-2 first:rounded-t-xl last:rounded-b-xl'
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-5'>
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className={`p-4 rounded-2xl shadow-sm border ${card.dark ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'} flex flex-col justify-between hover:shadow-md transition-all`}
          >
            <div className={`flex items-center gap-2 mb-2 ${card.dark ? 'text-neutral-400' : card.color}`}>
              {card.icon}
              <span className={`text-[10px] font-bold uppercase tracking-wider ${card.dark ? 'text-neutral-300' : ''}`}>
                {card.label}
              </span>
            </div>
            <div className={`text-2xl md:text-3xl font-bold ${card.dark ? 'text-white' : 'text-gray-900'}`}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs + Filters */}
      <div className='bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-4'>
        {/* Tabs */}
        <div className='flex gap-2 mb-4'>
          <button
            onClick={() => { setActiveTab('service'); setCurrentPage(1); }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === 'service' ? 'bg-indigo-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Service Leads ({data.total_service_leads || 0})
          </button>
          <button
            onClick={() => { setActiveTab('business'); setCurrentPage(1); }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === 'business' ? 'bg-indigo-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Business Leads ({data.total_business_leads || 0})
          </button>
        </div>

        {/* Filter row */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'>
          {/* Search */}
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
            <input
              type='text'
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder='Search by name, contact…'
              className='w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-[13px]'
            />
          </div>

          {/* Platform */}
          <select
            value={platformFilter}
            onChange={e => setPlatformFilter(e.target.value)}
            className='px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-[13px]'
          >
            {uniquePlatforms.map(p => (
              <option key={p} value={p}>{p === 'All' ? 'All Platforms' : p.charAt(0).toUpperCase() + p.slice(1)}</option>
            ))}
          </select>

          {/* Date Period select */}
          <div className='flex flex-col gap-2'>
            <div className='flex items-center gap-2'>
              <Calendar className='w-4 h-4 text-gray-400 shrink-0' />
              <select
                value={dateFilterType}
                onChange={e => setDateFilterType(e.target.value)}
                className='flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-[13px] bg-gray-50'
              >
                <option value='all'>All Dates</option>
                <option value='today'>Today</option>
                <option value='month'>This Month</option>
                <option value='custom'>Custom Range</option>
              </select>
            </div>

            {/* Sub-inputs */}
            {dateFilterType === 'month' && (
              <div className='relative'>
                <Calendar className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4' />
                <input
                  type='month'
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                  className='w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg outline-none text-[13px]'
                />
              </div>
            )}
            {dateFilterType === 'custom' && (
              <div className='flex gap-2 items-center'>
                <div className='relative flex-1'>
                  <Calendar className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5' />
                  <input
                    type='date'
                    value={fromDate}
                    onChange={e => setFromDate(e.target.value)}
                    className='w-full pl-8 pr-2 py-1.5 border border-gray-300 rounded-lg outline-none text-[12px]'
                  />
                </div>
                <span className='text-xs text-gray-400 shrink-0'>to</span>
                <div className='relative flex-1'>
                  <Calendar className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5' />
                  <input
                    type='date'
                    value={toDate}
                    onChange={e => setToDate(e.target.value)}
                    className='w-full pl-8 pr-2 py-1.5 border border-gray-300 rounded-lg outline-none text-[12px]'
                  />
                </div>
              </div>
            )}
          </div>

          {/* Clear button */}
          <div className='flex items-start'>
            {(search || platformFilter !== 'All' || dateFilterType !== 'all') && (
              <button
                onClick={() => {
                  setSearch('');
                  setPlatformFilter('All');
                  setDateFilterType('all');
                  setFromDate(todayStr);
                  setToDate(todayStr);
                  setSelectedMonth(currentYearMonth);
                }}
                className='flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 text-[13px] font-medium'
              >
                <X className='w-3.5 h-3.5' /> Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ─── DESKTOP TABLE ─────────────────────────────────── */}
      {activeTab === 'service' ? (
        <>
          <div className='hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-4'>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50 border-b border-gray-200'>
                  <tr>
                    {['#', 'Name', 'Service', 'Age', 'Gender', 'Weight', 'Contact', 'Platform', 'Created'].map(h => (
                      <th key={h} className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {loading ? (
                    <tr><td colSpan='9' className='py-12 text-center'>
                      <div className='flex justify-center'><div className='w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin' /></div>
                    </td></tr>
                  ) : paginated.length === 0 ? (
                    <tr><td colSpan='9' className='py-12 text-center text-gray-400 text-sm'>No leads found</td></tr>
                  ) : paginated.map(lead => (
                    <tr key={lead.id} className='hover:bg-gray-50 transition-colors'>
                      <td className='px-4 py-3 text-[13px] text-gray-500 font-mono'>{lead.id}</td>
                      <td className='px-4 py-3'>
                        <div className='text-[13px] font-semibold text-gray-900'>{lead.name || 'N/A'}</div>
                        {lead.address && <div className='text-[11px] text-gray-400 flex items-center gap-1'><MapPin className='w-3 h-3' />{lead.address}</div>}
                      </td>
                      <td className='px-4 py-3'>
                        <span className='px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-[11px] font-semibold rounded-full'>
                          {lead.service_type || 'N/A'}
                        </span>
                      </td>
                      <td className='px-4 py-3 text-[13px] text-gray-700'>{lead.age || '—'}</td>
                      <td className='px-4 py-3'>
                        <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${lead.gender === 'Female' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                          {lead.gender || '—'}
                        </span>
                      </td>
                      <td className='px-4 py-3 text-[13px] text-gray-700'>{lead.weight ? `${lead.weight} kg` : '—'}</td>
                      <td className='px-4 py-3'>
                        <button
                          onClick={e => handleContactClick(e, lead)}
                          className='text-[13px] text-indigo-600 hover:underline flex items-center gap-1 group'
                          title='View chat history'
                        >
                          <Phone className='w-3 h-3' />{lead.contact || '—'}
                          <MessageCircle className='w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity' />
                        </button>
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex items-center gap-1.5 text-[12px] text-gray-600'>
                          {platformIcon(lead.platform)}
                          {lead.platform ? lead.platform.charAt(0).toUpperCase() + lead.platform.slice(1) : '—'}
                        </div>
                      </td>
                      <td className='px-4 py-3 text-[12px] text-gray-500'>
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* MOBILE CARDS - service */}
          <div className='lg:hidden space-y-3 mb-4'>
            {loading ? (
              <div className='flex justify-center py-12'><div className='w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin' /></div>
            ) : paginated.length === 0 ? (
              <div className='text-center py-12 text-gray-400'>No leads found</div>
            ) : paginated.map(lead => (
              <div key={lead.id} className='bg-white rounded-2xl shadow-sm border border-gray-200 p-4'>
                <div className='flex justify-between items-start mb-3'>
                  <div>
                    <div className='text-[15px] font-bold text-gray-900'>{lead.name || 'N/A'}</div>
                    <div className='text-[11px] text-gray-400 font-mono'>#{lead.id}</div>
                  </div>
                  <span className='px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-[11px] font-semibold rounded-full'>
                    {lead.service_type || 'N/A'}
                  </span>
                </div>
                <div className='grid grid-cols-2 gap-2 text-[12px]'>
                  <div className='flex items-center gap-1.5 text-gray-600'>
                    <button
                      onClick={e => handleContactClick(e, lead)}
                      className='hover:underline flex items-center gap-1'
                    >
                      <Phone className='w-3.5 h-3.5 text-indigo-500' />{lead.contact || '—'}
                    </button>
                  </div>
                  <div className='flex items-center gap-1.5 text-gray-600'>
                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${lead.gender === 'Female' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                      {lead.gender || '—'}
                    </span>
                    {lead.age && <span className='text-gray-500'>{lead.age} yrs</span>}
                  </div>
                  {lead.weight && (
                    <div className='text-gray-600'>⚖️ {lead.weight} kg</div>
                  )}
                  <div className='flex items-center gap-1.5 text-gray-600'>
                    {platformIcon(lead.platform)}
                    {lead.platform ? lead.platform.charAt(0).toUpperCase() + lead.platform.slice(1) : '—'}
                  </div>
                </div>
                <div className='mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-400'>
                  {lead.created_at ? new Date(lead.created_at).toLocaleString('en-IN') : '—'}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* DESKTOP TABLE - business */}
          <div className='hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-4'>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50 border-b border-gray-200'>
                  <tr>
                    {['#', 'Name', 'Age', 'Location', 'Current Work', 'Contact', 'Existing Biz', 'Biz Name', 'Platform', 'Created'].map(h => (
                      <th key={h} className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {loading ? (
                    <tr><td colSpan='10' className='py-12 text-center'>
                      <div className='flex justify-center'><div className='w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin' /></div>
                    </td></tr>
                  ) : paginated.length === 0 ? (
                    <tr><td colSpan='10' className='py-12 text-center text-gray-400 text-sm'>No leads found</td></tr>
                  ) : paginated.map(lead => (
                    <tr key={lead.id} className='hover:bg-gray-50 transition-colors'>
                      <td className='px-4 py-3 text-[13px] text-gray-500 font-mono'>{lead.id}</td>
                      <td className='px-4 py-3 text-[13px] font-semibold text-gray-900'>{lead.name || 'N/A'}</td>
                      <td className='px-4 py-3 text-[13px] text-gray-700'>{lead.age || '—'}</td>
                      <td className='px-4 py-3'>
                        {lead.location ? (
                          <div className='flex items-center gap-1 text-[13px] text-gray-600'>
                            <MapPin className='w-3 h-3 text-gray-400' />{lead.location}
                          </div>
                        ) : '—'}
                      </td>
                      <td className='px-4 py-3'>
                        {lead.current_working ? (
                          <div className='flex items-center gap-1 text-[13px] text-gray-600'>
                            <Briefcase className='w-3 h-3 text-gray-400' />{lead.current_working}
                          </div>
                        ) : '—'}
                      </td>
                      <td className='px-4 py-3'>
                        <button
                          onClick={e => handleContactClick(e, lead)}
                          className='text-[13px] text-indigo-600 hover:underline flex items-center gap-1 group'
                          title='View chat history'
                        >
                          <Phone className='w-3 h-3' />{lead.contact || '—'}
                          <MessageCircle className='w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity' />
                        </button>
                      </td>
                      <td className='px-4 py-3 text-center'>
                        <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${lead.is_existing_business ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {lead.is_existing_business ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className='px-4 py-3 text-[13px] text-gray-600'>{lead.business_name || '—'}</td>
                      <td className='px-4 py-3'>
                        <div className='flex items-center gap-1.5 text-[12px] text-gray-600'>
                          {platformIcon(lead.platform)}
                          {lead.platform ? lead.platform.charAt(0).toUpperCase() + lead.platform.slice(1) : '—'}
                        </div>
                      </td>
                      <td className='px-4 py-3 text-[12px] text-gray-500'>
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* MOBILE CARDS - business */}
          <div className='lg:hidden space-y-3 mb-4'>
            {loading ? (
              <div className='flex justify-center py-12'><div className='w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin' /></div>
            ) : paginated.length === 0 ? (
              <div className='text-center py-12 text-gray-400'>No leads found</div>
            ) : paginated.map(lead => (
              <div key={lead.id} className='bg-white rounded-2xl shadow-sm border border-gray-200 p-4'>
                <div className='flex justify-between items-start mb-3'>
                  <div>
                    <div className='text-[15px] font-bold text-gray-900'>{lead.name || 'N/A'}</div>
                    <div className='text-[11px] text-gray-400 font-mono'>#{lead.id}</div>
                  </div>
                  <span className={`px-2.5 py-0.5 text-[11px] font-semibold rounded-full ${lead.is_existing_business ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {lead.is_existing_business ? 'Existing Biz' : 'New Lead'}
                  </span>
                </div>
                <div className='grid grid-cols-2 gap-2 text-[12px]'>
                  <div className='flex items-center gap-1.5 text-gray-600'>
                    <button
                      onClick={e => handleContactClick(e, lead)}
                      className='hover:underline flex items-center gap-1'
                    >
                      <Phone className='w-3.5 h-3.5 text-indigo-500' />{lead.contact || '—'}
                    </button>
                  </div>
                  {lead.location && (
                    <div className='flex items-center gap-1.5 text-gray-600'>
                      <MapPin className='w-3.5 h-3.5 text-gray-400' />{lead.location}
                    </div>
                  )}
                  {lead.current_working && (
                    <div className='flex items-center gap-1.5 text-gray-600 col-span-2'>
                      <Briefcase className='w-3.5 h-3.5 text-gray-400' />{lead.current_working}
                    </div>
                  )}
                  <div className='flex items-center gap-1.5 text-gray-600'>
                    {platformIcon(lead.platform)}
                    {lead.platform ? lead.platform.charAt(0).toUpperCase() + lead.platform.slice(1) : 'No Platform'}
                  </div>
                  {lead.age && <div className='text-gray-500'>Age: {lead.age}</div>}
                </div>
                <div className='mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-400'>
                  {lead.created_at ? new Date(lead.created_at).toLocaleString('en-IN') : '—'}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {!loading && filteredLeads.length > ITEMS_PER_PAGE && (
        <div className='flex items-center justify-between bg-white rounded-2xl shadow-sm border border-gray-200 px-4 py-3'>
          <div className='text-[12px] text-gray-500'>
            Showing <span className='font-semibold text-gray-800'>{((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredLeads.length)}</span> of <span className='font-semibold text-gray-800'>{filteredLeads.length}</span> leads
          </div>
          <div className='flex gap-1'>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className='px-3 py-1.5 text-[12px] rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors'
            >
              Prev
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
              if (page < 1 || page > totalPages) return null;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 text-[12px] rounded-lg border transition-colors ${currentPage === page ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  {page}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className='px-3 py-1.5 text-[12px] rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors'
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Close export menu on outside click */}
      {showExportMenu && (
        <div className='fixed inset-0 z-10' onClick={() => setShowExportMenu(false)} />
      )}

      {/* ─── CHAT HISTORY DRAWER ──────────────────────────────────────────── */}
      {chatOpen && (
        <div className='fixed inset-0 z-50 flex justify-end'>
          {/* Backdrop */}
          <div
            className='absolute inset-0 bg-black/40 backdrop-blur-sm'
            onClick={() => setChatOpen(false)}
          />

          {/* Drawer panel */}
          <div className='relative w-full max-w-md bg-white shadow-2xl flex flex-col h-full'>

            {/* Drawer header */}
            <div className='flex items-center gap-3 px-4 py-4 border-b border-gray-100 bg-indigo-600 text-white'>
              <button
                onClick={() => setChatOpen(false)}
                className='p-1.5 rounded-lg hover:bg-indigo-500 transition-colors'
              >
                <ArrowLeft className='w-5 h-5' />
              </button>
              <div className='flex-1 min-w-0'>
                <div className='font-semibold text-[15px] truncate'>{chatLead?.name || 'Lead'}</div>
                <div className='text-[11px] text-indigo-200 flex items-center gap-1'>
                  {chatLead?.platform && (
                    <>
                      {chatLead.platform === 'instagram'
                        ? <Instagram className='w-3 h-3' />
                        : chatLead.platform === 'facebook'
                          ? <Facebook className='w-3 h-3' />
                          : <Globe className='w-3 h-3' />}
                      <span className='capitalize'>{chatLead.platform}</span>
                    </>
                  )}
                  {chatLead?.contact && <span className='ml-1'>• {chatLead.contact}</span>}
                </div>
              </div>
              <div className='text-[11px] text-indigo-200 shrink-0'>
                {chatMessages.length} msgs
              </div>
            </div>

            {/* Chat body */}
            <div className='flex-1 overflow-y-auto px-4 py-4 bg-gray-50 space-y-3'>
              {chatLoading ? (
                <div className='flex justify-center items-center h-full'>
                  <div className='w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin' />
                </div>
              ) : chatError ? (
                <div className='flex flex-col items-center justify-center h-full text-gray-400 gap-2'>
                  <MessageCircle className='w-10 h-10 opacity-30' />
                  <p className='text-sm'>{chatError}</p>
                  <button
                    onClick={() => fetchChatHistory(chatLead)}
                    className='text-indigo-600 text-sm underline'
                  >
                    Retry
                  </button>
                </div>
              ) : chatMessages.length === 0 ? (
                <div className='flex flex-col items-center justify-center h-full text-gray-400 gap-2'>
                  <MessageCircle className='w-10 h-10 opacity-30' />
                  <p className='text-sm'>No messages found</p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => {
                  const isOut = msg.direction === 'outbound';
                  const time = msg.created_at
                    ? new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                    : '';
                  const date = msg.created_at
                    ? new Date(msg.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })
                    : '';

                  // Show date separator
                  const prevMsg = chatMessages[idx - 1];
                  const prevDate = prevMsg?.created_at
                    ? new Date(prevMsg.created_at).toDateString()
                    : null;
                  const thisDate = msg.created_at ? new Date(msg.created_at).toDateString() : null;
                  const showDateSep = idx === 0 || prevDate !== thisDate;

                  return (
                    <div key={msg.id}>
                      {showDateSep && (
                        <div className='flex items-center gap-2 my-3'>
                          <div className='flex-1 h-px bg-gray-200' />
                          <span className='text-[10px] text-gray-400 px-2 bg-gray-50 rounded-full border border-gray-200'>
                            {date}
                          </span>
                          <div className='flex-1 h-px bg-gray-200' />
                        </div>
                      )}
                      <div className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl shadow-sm text-[13px] leading-relaxed ${
                            isOut
                              ? 'bg-indigo-600 text-white rounded-br-sm'
                              : 'bg-white text-gray-800 border border-gray-100 rounded-bl-sm'
                          }`}
                        >
                          {msg.message_type === 'button_response' && (
                            <span className={`inline-block text-[10px] font-semibold mb-1 px-2 py-0.5 rounded-full ${
                              isOut ? 'bg-indigo-500 text-indigo-100' : 'bg-gray-100 text-gray-500'
                            }`}>
                              Button Response
                            </span>
                          )}
                          <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.content}</p>
                          <div className={`text-[10px] mt-1 text-right ${
                            isOut ? 'text-indigo-200' : 'text-gray-400'
                          }`}>
                            {time}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Auto-scroll anchor */}
            <div ref={chatBottomRef} />

            {/* Reply input bar */}
            <div className='px-3 py-3 border-t border-gray-200 bg-white'>
              {chatLead?.platform_user_id ? (
                <div className='flex items-end gap-2'>
                  <textarea
                    rows={1}
                    value={replyText}
                    onChange={e => {
                      setReplyText(e.target.value);
                      // auto-grow
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    placeholder='Type a message… (Enter to send)'
                    className='flex-1 resize-none px-3.5 py-2.5 border border-gray-200 rounded-2xl text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 max-h-[120px] overflow-y-auto'
                    style={{ height: '40px' }}
                    disabled={replySending}
                  />
                  <button
                    onClick={sendReply}
                    disabled={!replyText.trim() || replySending}
                    className='flex items-center justify-center w-10 h-10 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0'
                    title='Send'
                  >
                    {replySending
                      ? <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                      : <Send className='w-4 h-4' />}
                  </button>
                </div>
              ) : (
                <div className='flex items-center gap-2 text-[12px] text-gray-400'>
                  <Send className='w-3.5 h-3.5' />
                  <span>No platform user ID — reply unavailable for this lead</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiLeads;
